import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import hubRqServiceApi from '../api/hub-rq.service.api';

export const hubKeys = {
  all: ['hub'] as const,
  item: (id: string) => [...hubKeys.all, 'item', id] as const,
};

/**
 * Query options for one hub item's metadata, shared by `useHubKnowledgeItem` and
 * batched lookups (`useQueries`). Metadata is stable for a session.
 *
 * @param id - The hub item id (the source item's id).
 * @returns Options for `useQuery` / `useQueries`.
 * @example
 * useQueries({ queries: ids.map(hubItemQuery) });
 */
export function hubItemQuery(id: string) {
  return {
    queryKey: hubKeys.item(id),
    queryFn: ({ signal }: { signal?: AbortSignal }) => hubRqServiceApi.getItem(id, { signal }),
    staleTime: Infinity,
    // The source row is secondary to the answer: one retry, then it just stays
    // hidden. A 404 (item not in the hub) is terminal — don't retry it.
    retry: (failureCount: number, error: Error) =>
      isAxiosError(error) && error.response?.status === 404 ? false : failureCount < 1,
  };
}

/**
 * Resolve a hub knowledge item's metadata by id. Goes through the hub-access
 * policy, so it works cross-org where the caller's own knowledge store can't —
 * an org only ever sees what's shared into the hub, never another org's raw KB.
 * Disabled (no fetch) unless an id is given.
 *
 * @param id - The hub item id, or nothing to stay idle.
 * @returns The item query; `data` is the hub copy once it lands.
 * @example
 * const { data: hubItem } = useHubKnowledgeItem(isHub ? card.documentId : undefined);
 */
export function useHubKnowledgeItem(id: string | undefined) {
  return useQuery({ ...hubItemQuery(id ?? ''), enabled: Boolean(id) });
}
